import { useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import type { MicrocycleInput } from '@shared/schemas';
import { addDays, todayISO } from '@shared/dates';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useEvents,
  useMicrocycles,
  useSaveMicrocycle,
  useSeasons,
  useSessions,
  useTeams,
  type MicrocycleRow,
} from '@/app/queries';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import { formatDate, formatDateRange } from '@/lib/format';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { Section } from '@/ui/Section';
import { AiWeekReview } from '@/routes/plan/AiWeekReview';
import { LoadProfile } from '@/ui/LoadProfile';
import { Chip } from '@/ui/Chip';
import { Matrix } from '@/ui/Matrix';

/** Monday of the week containing `iso`. Microcycles run Monday to Sunday. */
function weekStart(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const offset = (d.getUTCDay() + 6) % 7;
  return addDays(iso, -offset);
}

export function Plan({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const teamList = teams.data ?? [];
  const [teamId, setTeamId] = useState<string | null>(null);
  const activeTeamId = teamId ?? teamList[0]?.id ?? null;

  const today = todayISO();
  const [anchorWeek, setAnchorWeek] = useState(() => weekStart(today));
  const rangeFrom = addDays(anchorWeek, -28);
  const rangeTo = addDays(anchorWeek, 28);

  const cycles = useMicrocycles(activeTeamId ?? undefined, rangeFrom, rangeTo);
  const sessions = useSessions(activeTeamId ?? undefined, rangeFrom, rangeTo);
  const events = useEvents(activeTeamId ?? undefined, rangeFrom, rangeTo);
  const seasons = useSeasons(activeTeamId ?? undefined);
  const save = useSaveMicrocycle();
  const { busy, formError, fieldError, submit } = useAuthForm();

  const [editing, setEditing] = useState<MicrocycleRow | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<MicrocycleInput | null>(null);

  const list = cycles.data ?? [];
  const sessionList = sessions.data ?? [];
  const matchDates = new Set(
    (events.data ?? [])
      .filter((e) => (e.kind === 'match' || e.kind === 'friendly') && e.status !== 'cancelled')
      .map((e) => e.onDate),
  );

  const set = <K extends keyof MicrocycleInput>(k: K) => (value: MicrocycleInput[K]) =>
    setForm((f) => (f ? { ...f, [k]: value } : f));

  const startCreate = () => {
    if (!activeTeamId) return;
    const start = anchorWeek;
    setEditing(null);
    setForm({
      teamId: activeTeamId,
      name: '',
      startsOn: start,
      endsOn: addDays(start, 6),
      seasonId: seasons.data?.[0]?.id,
      days: [],
    } as MicrocycleInput);
    setOpen(true);
  };

  const startEdit = (cycle: MicrocycleRow) => {
    setEditing(cycle);
    setForm({
      teamId: cycle.teamId,
      name: cycle.name,
      startsOn: cycle.startsOn,
      endsOn: cycle.endsOn,
      anchorEventId: cycle.anchorEventId ?? undefined,
      objectiveSummary: cycle.objectiveSummary ?? undefined,
      adjustmentReason: cycle.adjustmentReason ?? undefined,
      days: cycle.days.map((d) => ({
        onDate: d.onDate,
        mdLabel: d.mdLabel ?? undefined,
        mainObjective: d.mainObjective ?? undefined,
        plannedLoad: d.plannedLoad ?? undefined,
        plannedRpe: d.plannedRpe ?? undefined,
        plannedDurationMin: d.plannedDurationMin ?? undefined,
        energySystemKey: d.energySystemKey ?? undefined,
        physicalEmphasis: d.physicalEmphasis ?? undefined,
        tacticalEmphasis: d.tacticalEmphasis ?? undefined,
        dayType: (d.dayType as MicrocycleInput['days'][number]['dayType']) ?? undefined,
        notes: d.notes ?? undefined,
      })),
    } as MicrocycleInput);
    setOpen(true);
  };

  /** Planned comes from the week's own day targets; actual from what was run. */
  const profileFor = useMemo(
    () => (cycle: MicrocycleRow) =>
      cycle.days.map((d) => {
        const session = sessionList.find((s) => s.onDate === d.onDate);
        return {
          date: d.onDate,
          mdLabel: d.mdLabel,
          plannedLoad: d.plannedLoad ?? session?.plannedLoad ?? null,
          actualLoad: session?.actualLoad ?? null,
          isMatch: matchDates.has(d.onDate),
          isToday: d.onDate === today,
        };
      }),
    [sessionList, matchDates, today],
  );

  if (teams.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (teamList.length === 0) {
    return (
      <>
        <PageHeader
          title={t('plan.title')}
          lead={t('plan.lead')}
          help={localePath(locale, '/guide/plan')}
        />
        <Panel title={t('plan.title')} explain="microcycle">
          <EmptyState title={t('teams.empty')} body={t('dashboard.noTeamBody')} />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('plan.title')}
        lead={t('plan.lead')}
        help={localePath(locale, '/guide/plan')}
        action={
          <Button variant="primary" onClick={startCreate}>
            {t('plan.newWeek')}
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {teamList.length > 1
          ? teamList.map((tm) => (
              <button
                key={tm.id}
                type="button"
                onClick={() => setTeamId(tm.id)}
                aria-pressed={tm.id === activeTeamId}
                className={`rounded-(--radius-control) border px-3 py-1.5 text-meta transition-colors ${
                  tm.id === activeTeamId
                    ? 'border-ink bg-ink text-paper'
                    : 'border-line bg-surface text-ink-muted hover:border-ink'
                }`}
              >
                {tm.name}
              </button>
            ))
          : null}
        {/* Stepping a week at a time is the most-used control on this screen
            and it was 30x36. The chevrons also said nothing to a screen reader. */}
        <div className="ms-auto flex gap-1">
          <Button
            variant="secondary"
            aria-label={t('common.previousWeek')}
            className="!min-w-11 !px-3 !text-meta"
            onClick={() => setAnchorWeek((w) => addDays(w, -7))}
          >
            <span aria-hidden="true">‹</span>
          </Button>
          <Button
            variant="secondary"
            className="!text-meta"
            onClick={() => setAnchorWeek(weekStart(today))}
          >
            {t('calendar.today')}
          </Button>
          <Button
            variant="secondary"
            aria-label={t('common.nextWeek')}
            className="!min-w-11 !px-3 !text-meta"
            onClick={() => setAnchorWeek((w) => addDays(w, 7))}
          >
            <span aria-hidden="true">›</span>
          </Button>
        </div>
      </div>

      {open && form ? (
        <div className="mb-5">
          <Panel title={editing ? t('plan.editWeek') : t('plan.newWeek')}>
            <form
              noValidate
              onSubmit={async (e) => {
                e.preventDefault();
                const ok = await submit(async () => {
                  await save.mutateAsync({ microcycleId: editing?.id, input: form });
                });
                if (ok) {
                  setOpen(false);
                  setEditing(null);
                }
              }}
            >
              {formError ? (
                <div className="px-4 pt-4">
                  <Notice tone="risk">{formError}</Notice>
                </div>
              ) : null}

              <Section title={t('plan.newWeek')}>
                <div className="grid gap-x-5 gap-y-2 sm:grid-cols-3">
                  <Field label={t('plan.name')} error={fieldError('name')}>
                    {(p) => (
                      <TextInput
                        {...p}
                        value={form.name}
                        onChange={(e) => set('name')(e.target.value)}
                        required
                        autoFocus
                      />
                    )}
                  </Field>
                  <Field label={t('plan.startsOn')} error={fieldError('startsOn')}>
                    {(p) => (
                      <TextInput
                        {...p}
                        type="date"
                        value={form.startsOn}
                        onChange={(e) => set('startsOn')(e.target.value)}
                        required
                      />
                    )}
                  </Field>
                  <Field label={t('plan.endsOn')} error={fieldError('endsOn')}>
                    {(p) => (
                      <TextInput
                        {...p}
                        type="date"
                        value={form.endsOn}
                        onChange={(e) => set('endsOn')(e.target.value)}
                        required
                      />
                    )}
                  </Field>
                  <div className="sm:col-span-3">
                    <Field
                      label={t('plan.objective')}
                      hint={t('plan.objectiveHint')}
                      error={fieldError('objectiveSummary')}
                    >
                      {(p) => (
                        <TextArea
                          {...p}
                          value={form.objectiveSummary ?? ''}
                          onChange={(e) => set('objectiveSummary')(e.target.value || undefined)}
                        />
                      )}
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label={t('plan.anchor')} optional>
                      {(p) => (
                        <SelectInput
                          {...p}
                          value={form.anchorEventId ?? ''}
                          onChange={(e) => set('anchorEventId')(e.target.value || undefined)}
                        >
                          <option value="">{t('plan.noAnchor')}</option>
                          {(events.data ?? [])
                            .filter((ev) => ev.kind === 'match' || ev.kind === 'friendly')
                            .map((ev) => (
                              <option key={ev.id} value={ev.id}>
                                {formatDate(ev.onDate, locale)} — {ev.opponent ?? t('calendar.kind_match')}
                              </option>
                            ))}
                        </SelectInput>
                      )}
                    </Field>
                  </div>
                  {editing ? (
                    <div className="sm:col-span-3">
                      <Field
                        label={t('plan.adjustmentReason')}
                        hint={t('plan.adjustmentReasonHint')}
                        optional
                      >
                        {(p) => (
                          <TextInput
                            {...p}
                            value={form.adjustmentReason ?? ''}
                            onChange={(e) => set('adjustmentReason')(e.target.value || undefined)}
                          />
                        )}
                      </Field>
                    </div>
                  ) : null}
                </div>
              </Section>

              {form.days.length > 0 ? (
                <Section title={t('plan.day')} explain="md_label">
                  {/*
                    A card per day below `md`. The table was 736px wide on a
                    390px screen with its three inputs shrunk to about 32px, so
                    filling in a week meant scrolling sideways to reach each
                    field. The week's shape at a glance is the load profile
                    above this, not these rows, so transposing costs nothing.
                  */}
                  <Matrix
                    rows={form.days}
                    rowKey={(d) => d.onDate}
                    caption={t('plan.day')}
                    columns={[
                      {
                        key: 'day',
                        label: t('plan.day'),
                        heading: true,
                        cell: (d) => (
                          <span className="tabular whitespace-nowrap">
                            {formatDate(d.onDate, locale)}
                          </span>
                        ),
                      },
                      {
                        key: 'mdLabel',
                        label: t('plan.mdLabel'),
                        cell: (d, i) => (
                          <TextInput
                            value={d.mdLabel ?? ''}
                            onChange={(e) =>
                              set('days')(
                                form.days.map((x, j) =>
                                  j === i ? { ...x, mdLabel: e.target.value || undefined } : x,
                                ),
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: 'dayType',
                        label: t('plan.dayType'),
                        cell: (d, i) => (
                          <SelectInput
                            value={d.dayType ?? ''}
                            onChange={(e) =>
                              set('days')(
                                form.days.map((x, j) =>
                                  j === i
                                    ? {
                                        ...x,
                                        dayType: (e.target.value || undefined) as typeof x.dayType,
                                      }
                                    : x,
                                ),
                              )
                            }
                          >
                            <option value="">{t('sessions.none')}</option>
                            {(['rest', 'recovery', 'normal', 'high'] as const).map((k) => (
                              <option key={k} value={k}>
                                {t(`plan.dayType_${k}`)}
                              </option>
                            ))}
                          </SelectInput>
                        ),
                      },
                      {
                        key: 'mainObjective',
                        label: t('plan.mainObjective'),
                        cell: (d, i) => (
                          <TextInput
                            value={d.mainObjective ?? ''}
                            onChange={(e) =>
                              set('days')(
                                form.days.map((x, j) =>
                                  j === i
                                    ? { ...x, mainObjective: e.target.value || undefined }
                                    : x,
                                ),
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: 'plannedLoad',
                        label: t('plan.plannedLoad'),
                        cell: (d, i) => (
                          <TextInput
                            type="number"
                            min={0}
                            max={3000}
                            value={d.plannedLoad ?? ''}
                            onChange={(e) =>
                              set('days')(
                                form.days.map((x, j) =>
                                  j === i
                                    ? {
                                        ...x,
                                        plannedLoad:
                                          e.target.value === '' ? undefined : Number(e.target.value),
                                      }
                                    : x,
                                ),
                              )
                            }
                          />
                        ),
                      },
                    ]}
                  />
                </Section>
              ) : null}

              <div className="flex flex-wrap gap-2 border-t border-line px-4 py-4">
                <Button type="submit" variant="primary" busy={busy}>
                  {t('plan.save')}
                </Button>
                <Button
                  type="button"
                  variant="quiet"
                  onClick={() => {
                    setOpen(false);
                    setEditing(null);
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </Panel>
        </div>
      ) : null}

      {cycles.isLoading ? (
        <p className="flex items-center gap-2 text-ink-muted">
          <Spinner /> {t('common.loading')}
        </p>
      ) : list.length === 0 ? (
        <Panel title={t('plan.title')}>
          <EmptyState
            title={t('plan.empty')}
            body={t('plan.emptyBody')}
            action={
              <Button variant="primary" onClick={startCreate}>
                {t('plan.newWeek')}
              </Button>
            }
          />
        </Panel>
      ) : (
        <div className="grid gap-5">
          {list.map((cycle) => {
            const profile = profileFor(cycle);
            const plannedTotal = profile.reduce((s, d) => s + (d.plannedLoad ?? 0), 0);
            const actualTotal = profile.reduce((s, d) => s + (d.actualLoad ?? 0), 0);
            return (
              <Panel
                key={cycle.id}
                title={cycle.name}
                count={formatDateRange(cycle.startsOn, cycle.endsOn, locale)}
                // A season of weeks, open all at once, was six panels and four
                // and a half screens. The week a coach is in is the one he came
                // for; the others are a tap each, and still say their name,
                // their dates and what they were for while closed.
                collapsible
                storeKey={`plan.week.${cycle.id}`}
                defaultOpen={cycle.startsOn <= today && today <= cycle.endsOn}
                summary={cycle.objectiveSummary || undefined}
                action={
                  <Button
                    variant="secondary"
                    className="!text-meta"
                    onClick={() => startEdit(cycle)}
                  >
                    {t('plan.editWeek')}
                  </Button>
                }
              >
                {cycle.objectiveSummary ? (
                  <p className="border-b border-line px-4 py-3 text-ink-muted">
                    {cycle.objectiveSummary}
                  </p>
                ) : null}

                <LoadProfile days={profile} />

                {/* Reading the week back against the week that was planned. */}
                <div className="border-t border-line px-4 py-3">
                  <AiWeekReview teamId={cycle.teamId} from={cycle.startsOn} to={cycle.endsOn} />
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line px-4 py-3 text-meta">
                  <span className="tabular text-ink-muted">
                    {t('plan.totalPlanned', { value: Math.round(plannedTotal) })}
                  </span>
                  <span className="tabular text-ink">
                    {t('plan.totalActual', { value: Math.round(actualTotal) })}
                  </span>
                  {cycle.adjustmentReason ? (
                    <Chip muted>{cycle.adjustmentReason}</Chip>
                  ) : null}
                  <span className="ms-auto text-ink-faint">{t('plan.loadProfileHint')}</span>
                </div>
              </Panel>
            );
          })}
        </div>
      )}
    </>
  );
}
